import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { AddressBookItem, AddressBookSearchParams, AddressBookState } from '../address-book-params.interface';
import { Store } from '@ngrx/store';
import { IState } from '../../shared/models/state';
import { GetAddressBookAction, ResetAddressBookAction, SetAddressBookParamsAction } from '../address-book.actions';
import { getWholeAddressBookSelector } from '../address-book.reducer';

import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { PaginatedData, SortChange } from '../../shared/models/table';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'cc-address-book-list',
  templateUrl: './address-book-list.component.html',
  styleUrls: ['./address-book-list.component.scss']
})
export class AddressBookListComponent implements OnInit, OnDestroy {
  public displayedColumns = [
    'contactName',
    'contactSurname',
    'msisdn',
    'contactEmail',
    'companyName',
    'contactDetails'
  ];
  public msisdns: any;
  public dataSource: MatTableDataSource<AddressBookItem>;
  public filters: AddressBookSearchParams;
  public pageSizeOptions = [10, 20, 100];
  public keepFilters = false;
  public phraseControl = new FormControl('');
  private phraseControlSubscription: Subscription;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(private route: ActivatedRoute, private store: Store<IState>, private location: Location) {}

  ngOnInit() {
    this.keepFilters = this.route.snapshot.queryParamMap.get('keepFilters') === 'true';
    this.msisdns = this.route.snapshot.data['msisdns'];
    this.dataSource = new MatTableDataSource([]);
    this.dataSource.data = this.route.snapshot.data['addressBook'];

    this.store.select(getWholeAddressBookSelector).subscribe((state: AddressBookState) => {
      const { addressBook, params } = state;
      this.filters = { ...params };
      this.updateSearchPhrase();
      this.updateDataSource(addressBook);
      this.updatePaginator(addressBook, params);
    });

    this.setupInitialData();
    this.changeDataOnSearchPhraseChanges();
  }

  ngOnDestroy() {
    if (!this.keepFilters) {
      this.store.dispatch(new ResetAddressBookAction());
    }
    if (this.phraseControlSubscription) {
      this.phraseControlSubscription.unsubscribe();
    }
  }

  addFilter(type, value) {
    this.filters[type] = value;
    this.resetPager();
    this.store.dispatch(new SetAddressBookParamsAction(this.filters));
  }

  changePage(event: PageEvent) {
    this.filters.pager = {
      limit: event.pageSize,
      page: event.pageIndex
    };
    this.store.dispatch(new SetAddressBookParamsAction(this.filters));
  }

  sortChange({ active, direction }: SortChange) {
    this.filters.order = direction.toUpperCase() || null;
    this.filters.sortBy = active;
    this.resetPager();
    this.store.dispatch(new SetAddressBookParamsAction(this.filters));
  }

  getNumbersCountry(number: string) {
    if (!number) {
      return '';
    }
    const parsed = parsePhoneNumberFromString(number.toString());
    return (parsed && parsed.country) || '';
  }

  formatNumbers(numbers) {
    return numbers
      .slice(1)
      .join()
      .replaceAll(',', '\n');
  }

  toggleContactsWithEmptyNameSearch(event: MatCheckboxChange) {
    this.filters.showContactsWithEmptyName = event.checked;
    this.store.dispatch(new SetAddressBookParamsAction(this.filters));
  }

  private updatePaginator(addressBook: PaginatedData<AddressBookItem>, params: AddressBookSearchParams) {
    this.paginator.length = addressBook.total;
    if (params && params.pager) {
      const { limit, page } = params.pager;
      this.paginator.pageIndex = page;
      this.paginator.pageSize = limit;
    }
  }

  private setupInitialData() {
    if (this.keepFilters) {
      this.keepFilters = false;
    } else {
      this.store.dispatch(new GetAddressBookAction(this.filters));
    }
  }

  private changeDataOnSearchPhraseChanges() {
    this.phraseControlSubscription = this.phraseControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(phrase => {
        this.addFilter('phrase', phrase);
      });
  }

  private updateSearchPhrase() {
    const isInitialRender = !this.phraseControl.value;
    if (isInitialRender) {
      this.phraseControl.setValue(this.filters.phrase);
    } else {
      // We don't need to sync input, because value from form control is always up to date.
    }
  }

  private updateDataSource(addressBook: PaginatedData<AddressBookItem>) {
    this.dataSource.data = addressBook.data.map(item => ({
      ...item,
      msisdn: item.msisdns[0]
    }));
  }

  private resetPager() {
    this.filters.pager.page = 0;
  }

  contactDetailsClicked() {
    this.location.replaceState('/address-book?keepFilters=true');
    this.keepFilters = true;
  }
}
